import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { useApiMutation } from './useApi.js';

const CURRENT_KEY = ['shift', 'current'];

export { useShiftUI } from './shiftUI.js';

/** الشفت المفتوح للمستخدم الحالي + هل الشفت إلزامي. */
export function useCurrentShift() {
  return useQuery({
    queryKey: CURRENT_KEY,
    queryFn: async () => {
      const { data } = await api.get('/shifts/current');
      return data || { shift: null, requireShift: true };
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true
  });
}

/** يعيد { shift, requireShift, blocked } — blocked = العمليات النقدية ممنوعة الآن. */
export function useShiftStatus() {
  const q = useCurrentShift();
  const shift = q.data?.shift || null;
  const requireShift = q.data?.requireShift !== false;
  return {
    shift,
    requireShift,
    loading: q.isLoading,
    blocked: !q.isLoading && requireShift && !shift,
    cashboxId: shift ? String(shift.cashboxId?._id || shift.cashboxId) : null
  };
}

function invalidateShift(qc) {
  qc.invalidateQueries({ queryKey: ['shift'] });
  qc.invalidateQueries({ queryKey: ['shifts'] });
  qc.invalidateQueries({ queryKey: ['cashboxes'] });
}

export function useOpenShift() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/shifts/open', body).then((r) => r.data),
    successMessage: 'تم فتح الشفت',
    onSuccess: () => invalidateShift(qc)
  });
}

export function useCloseShift() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => api.post(`/shifts/${id}/close`, body).then((r) => r.data),
    // خطأ «صفر بلا تأكيد» يُعالَج في النافذة بسؤال تأكيد، لا بتنبيه عام
    errorToast: false,
    onSuccess: () => invalidateShift(qc)
  });
}

export function useApproveVariance() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (id) => api.post(`/shifts/${id}/approve-variance`).then((r) => r.data),
    successMessage: 'تم اعتماد الفرق',
    onSuccess: () => invalidateShift(qc)
  });
}

export function useShiftReport(id, enabled = true) {
  return useQuery({
    queryKey: ['shifts', 'report', id],
    enabled: !!id && enabled,
    queryFn: async () => {
      const { data } = await api.get(`/shifts/${id}/report`);
      return data;
    },
    staleTime: 5_000
  });
}

// أسماء مصادر حركات النقد في التقارير
export const SOURCE_LABELS = {
  entry: 'رسوم دخول',
  checkout: 'تسوية وخروج',
  card_topup: 'تعبئة كروت',
  invoice_payment: 'تسديد فواتير',
  invoice_refund: 'إلغاء فواتير (مرتجع)',
  expense: 'مصاريف',
  expense_reversal: 'إلغاء مصاريف',
  revenue: 'إيرادات يدوية',
  revenue_reversal: 'إلغاء إيرادات',
  purchase: 'دفعات مشتريات',
  purchase_reversal: 'إلغاء مشتريات',
  transfer_in: 'تحويل وارد',
  transfer_out: 'تحويل صادر (سحب)',
  adjustment: 'تعديل يدوي',
  shift_float: 'عهدة الشفت',
  shift_open_adjust: 'فرق الدرج عند الفتح',
  shift_variance: 'فرق الإقفال',
  shift_deposit: 'ترحيل نقد الشفت',
  other: 'أخرى'
};

export const POS_TYPE_LABELS = {
  play_fee: 'رسوم لعب ودخول',
  cafeteria: 'الكافتيريا',
  toys: 'محل الألعاب',
  manual: 'يدوي',
  checkout: 'تسوية'
};
